<template>
    <b-card>
        <b-card-body>
            <b-card-header>
                <b-row>
                    <b-col cols="9">
                        <b-card-title>
                            <font-awesome-icon
                                    :icon="['fas', 'chart-bar']"
                                    size="sm"
                            ></font-awesome-icon>
                            &nbsp;{{data.actions.dueDate.title}}
                        </b-card-title>
                    </b-col>
                    <b-col cols="3" class="text-right">
                        <b-dropdown>
                            <template slot="button-content">
                                <font-awesome-icon
                                        :icon="['far', 'calendar-alt']"
                                        size="sm"
                                ></font-awesome-icon>
                                Select timescale
                            </template>
                            <b-dropdown-item v-for="item in timescales"
                                             :value="item.value"
                                             @click="redrawChart(item.value)"
                            >
                                {{item.label}}
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-col>
                </b-row>
            </b-card-header>
            <b-card-text>
                <data-bar v-if="dueDateLoaded"
                          :chart-data="dueDateChartData"
                >
                </data-bar>
            </b-card-text>
        </b-card-body>
        <vue-element-loading :active="!data.actions.dueDate.loaded" :is-full-screen="false"/>
    </b-card>
</template>

<script>
import DataBar from '@/components/Charts/DataBar';
import Report from '@/services/Report/Report';
import PropertyAssessmentType from '@/services/Property/PropertyAssessmentType';
import dayjs from 'dayjs';

export default {
  name: 'ActionDueDateWidget',
  components: {
    DataBar,
  },
  created() {
    this.getData();
  },
  data() {
    return {
      loading: true,

      data: {
        actions: {
          loaded: false,
          dueDate: {
            loaded: false,
            title: "Actions by Due Date",
            chartData: {
              title: 'Due By Month',
              loaded: false,
              key: [],
              labels: [],
              type: 'dueDate',
              dataStartDate: dayjs().format('YYYYMM'),
              dataEndDate: dayjs().add(1, 'year').format('YYYYMM'),
              datasets: [
                {
                  label: 'Level 1 Property',
                  riskProfile: 'high',
                  loading: false,
                  backgroundColor: '#4267B2',
                },
                {
                  label: 'Level 2 Property',
                  riskProfile: 'medium',
                  loading: false,
                  backgroundColor: '#F9C032',
                },
                {
                  label: 'Level 3 Property',
                  riskProfile: 'low',
                  loading: false,
                  backgroundColor: '#ED6B44',
                },
              ],
            },
          },
        },
      },
      dueDateData: {
        title: 'Due By Month',
        loaded: false,
        key: [],
        labels: [],
        type: 'dueDate',
        dataStartDate: dayjs().format('YYYYMM'),
        dataEndDate: dayjs().add(1, 'year').format('YYYYMM'),
        datasets: [],
      },
      dueDateChartData: {},
      actionProgressChartData: {},
      timescales: [
        {
            label: "12 months",
            value: 1,
        },
        {
          label: "24 months",
          value: 2,
        },
      ],
    };
  },
  computed: {
    dueDateLoaded() {
      return this.data.actions.dueDate.loaded &&
        (this.dueDateData.datasets.length === this.data.actions.dueDate.chartData.datasets.length);
    },
  },
  methods: {
    async getData() {
      this.loading = true;

      try {
        const datasets = await this.getDueDateDatasets();
        this.$set(this.dueDateData, 'datasets', datasets);
        this.formatDueDateChartData();

        this.data.actions.dueDate.loaded = true;
      } catch (e) {

      }
    },
    getDueDateDatasets() {
      return new Promise((resolve, reject) => {
        const { chartData } = this.data.actions.dueDate;

        const datasets = [];

        const datasetCount = chartData.datasets.length;

        chartData.datasets.forEach(async ({ riskProfile }, index) => {

          try {
            const { data } = await this.getReportData(riskProfile, chartData.type);

            const config = chartData.datasets[index];

            const { series } = data;

            const dataset = { ...config, ...data };

            const { dataStartDate, dataEndDate } = series;

            if (dataStartDate < chartData.dataStartDate) {
              //this.$set(this.dueDateData, 'dataStartDate', dataStartDate);
            }

            if (dataEndDate > chartData.dataEndDate) {
              //this.$set(this.dueDateData, 'dataEndDate', dataEndDate);
            }

            datasets.push(dataset);

            // Resolve the promise ONLY when all data has loaded
            if (datasets.length === datasetCount) {
              datasets.sort((a, b) => (a.label > b.label) ? 1 : -1);
              resolve(datasets);
            }

          } catch (e) {
            reject(e);
          }
        });
      });
    },
    getReportData(riskProfile, type) {
      return new Promise(async (resolve, reject) => {
        try {
          // for now, we hardcode FIRE as we only want to get these results
          const assessmentTypeId = PropertyAssessmentType.COMPLIANCE_TYPE_FIRE_ID();

          const { data } = await Report.get(type, riskProfile, assessmentTypeId);

          resolve(data);
        } catch (e) {
          // console.log(e);
          // TODO: Something here
          reject([]);
        }
      });
    },
    formatDueDateChartData() {

      const { datasets } = this.dueDateData;

      const startDate = dayjs(this.dueDateData.dataStartDate);
      const endDate = dayjs(this.dueDateData.dataEndDate);

      const dates = [];
      const keys = [];

      let month = startDate; // clone the startDate

      while (month.format('YYYYMM') <= endDate.format('YYYYMM')) {
        dates.push(month.format('MMM YYYY'));
        keys.push(month.format('YYYYMM'));
        month = month.add('1', 'month');
      }

      const chartDatasets = [];

      datasets.forEach((item, i) => {
        chartDatasets.push({
          data: this.formatDueDateDatasetValues(item.series, keys),
          label: item.label,
          backgroundColor: item.backgroundColor,
          hoverBackgroundColor: item.backgroundColor,
        });
      });

      this.dueDateChartData = {
        labels: dates,
        datasets: chartDatasets,
      };
    },
    formatDueDateDatasetValues(series, keys) {

      if (!series) {
        return [];
      }

      const values = series.data.map(row => row.count);
      const dates = series.data.map(row => row.dueDate);

      const formattedData = [];
      for (let i = 0; i < keys.length; i++) {
        const month = keys[i];
        const dataIndex = dates.indexOf(month);
        const dataValue = values[dataIndex] || 0;
        formattedData.push(dataValue);
      }

      return formattedData;
    },
    redrawChart(timescale) {
      this.dueDateData.dataEndDate = dayjs().add(timescale, 'year').format('YYYYMM');
      this.formatDueDateChartData();
    },
  },
};
</script>

<style scoped>
    .chart {
        margin-top: 3em;
        font-size: 13px;
    }
</style>
